import { bisectCenter, pointer, range } from "d3";
import type { ScaleLinear, ScaleTime } from "d3";
import {
  computeMonetaryValueByTradeFlow,
  formatTotalValue,
  type TradeDirection,
  type TradeFlow,
} from "@atlas-int/data";

export const tooltipElementReferenceId = "TooltipCaptureContainer";

export interface ChartMargin {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Determines the closest x-axis value based on mouse position
 */
export const determineClosestXValue = ({
  e,
  xScale,
  containerSize,
  chartMargin,
  setXAxisTooltipValue,
}: {
  e: React.MouseEvent<SVGElement>;
  xScale: ScaleLinear<number, number> | ScaleTime<number, number>;
  containerSize: { width: number; height: number } | undefined;
  setXAxisTooltipValue: (value: { year: number; x: number; y: number }) => void;
  chartMargin: ChartMargin;
}) => {
  if (!containerSize) return;

  const [xPos, yPos] = pointer(e.nativeEvent, e.nativeEvent.target);
  // Ensure x and y are within the chart bounds
  const x = Math.max(
    chartMargin.left,
    Math.min(xPos, containerSize.width - chartMargin.right),
  );
  const y = Math.max(
    chartMargin.top,
    Math.min(yPos, containerSize.height - chartMargin.bottom),
  );

  const [xScaleStart, xScaleEnd] = xScale.domain();
  const yearsRange = range(xScaleStart as number, (xScaleEnd as number) + 1, 1);
  const inverted = xScale.invert(x);
  const closestXScaleIndex = bisectCenter(yearsRange, inverted as number);
  const closestXScaleYearValue = yearsRange[closestXScaleIndex];

  // Call setXAxisTooltipValue with the new values (scale props are added by the caller)
  setXAxisTooltipValue({
    year: closestXScaleYearValue,
    x: xScale(closestXScaleYearValue as number) as number,
    y,
  });
};

/**
 * Formats the Y-axis value based on the layout type
 */
export const formatYAxisValue = ({
  tooltipData,
  overTimeLayout,
  yScale,
  y1,
  y2,
  tradeFlow,
  tradeDirection,
}: {
  tooltipData?: Record<string, unknown>;
  overTimeLayout?: string;
  yScale?: ScaleLinear<number, number>;
  y1?: number;
  y2?: number;
  tradeFlow?: TradeFlow;
  tradeDirection?: TradeDirection;
}): string => {
  try {
    if (tooltipData && overTimeLayout !== "share") {
      // Use the exact same value that appears in the tooltip
      const valueToUse = computeMonetaryValueByTradeFlow({
        datum: tooltipData,
        tradeFlow,
        tradeDirection,
      });
      return `$${formatTotalValue(valueToUse)}`;
    } else if (
      overTimeLayout === "share" &&
      tooltipData &&
      tooltipData.shareForYear !== undefined
    ) {
      // Use share value from tooltip data
      const percentValue = (tooltipData.shareForYear as number) * 100;
      return formatPercentage(percentValue);
    } else if (yScale && y1 !== undefined && y2 !== undefined) {
      // Fallback to scale-based calculation if tooltip data isn't available
      const yValueTop = yScale.invert(y2 - 10);
      const yValueBottom = yScale.invert(y1 - 10);
      const yValue = Math.abs(yValueBottom - yValueTop);

      if (overTimeLayout === "share") {
        const percentValue = yValue * 100;
        return formatPercentage(percentValue);
      } else {
        return `$${formatTotalValue(yValue)}`;
      }
    }
  } catch (error) {
    return "";
  }
  return "";
};

/**
 * Formats percentage values with appropriate precision
 */
export const formatPercentage = (percentValue: number): string => {
  if (percentValue < 0.1) {
    return `${percentValue.toFixed(3)}%`;
  } else if (percentValue < 1) {
    return `${percentValue.toFixed(2)}%`;
  } else if (percentValue < 10) {
    return `${percentValue.toFixed(1)}%`;
  } else {
    return `${Math.round(percentValue)}%`;
  }
};
